import { useCallback, useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Loader2, LogOut, Truck, MapPin, Phone, Mail, RefreshCw, Check, CheckCircle2, History } from "lucide-react";
import { DeliveredDialog } from "@/components/admin/DeliveredDialog";
import { DeliveryList } from "@/components/DeliveryList";
import { CustomPackages } from "@/components/admin/CustomPackages";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Seo } from "@/components/site/Section";
import { http, errorMessage, formatGBP } from "@/lib/api";
import { cn } from "@/lib/utils";

const TOKEN_KEY = "wlr_admin_token";
const auth = () => ({ headers: { Authorization: `Bearer ${sessionStorage.getItem(TOKEN_KEY)}` } });

const AdminHistory = ({ s, version }) => {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(null);
  const fetchRows = useCallback(() => {
    http.get(`/admin/subscriptions/${s.subscription_id}/deliveries`, auth()).then(({ data }) => setRows(data)).catch((e) => { toast.error(errorMessage(e)); setRows([]); });
  }, [s.subscription_id]);
  useEffect(() => {
    if (open) fetchRows();
  }, [open, version, fetchRows]);
  const toggle = () => setOpen(!open);
  return (
    <div className="mt-3">
      <button onClick={toggle} className="inline-flex items-center gap-2 text-sm text-[#D4A437] hover:underline" data-testid={`admin-history-toggle-${s.subscription_id}`}>
        <History className="h-4 w-4" />{open ? "Hide delivery history" : "Delivery history"}
      </button>
      {open && <div className="mt-3"><DeliveryList rows={rows} cfg={auth} photoUrl={(id) => `/admin/deliveries/${id}/photo`} showEmailed testid={`admin-history-${s.subscription_id}`} /></div>}
    </div>
  );
};

const Login = ({ onDone }) => {
  const [f, setF] = useState({ email: "", password: "" });
  const [state, setState] = useState({ loading: false, error: "" });
  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, error: "" });
    try {
      const { data } = await http.post("/admin/login", f);
      sessionStorage.setItem(TOKEN_KEY, data.token);
      onDone();
    } catch (err) {
      setState({ loading: false, error: errorMessage(err) });
    }
  };
  return (
    <form onSubmit={submit} className="mx-auto mt-10 max-w-md space-y-4 rounded-3xl border border-[#262626] bg-[#101010] p-8" data-testid="admin-login-form">
      <h1 className="font-display text-3xl font-bold">Admin sign in</h1>
      <div className="space-y-1.5">
        <Label htmlFor="admin-email">Email</Label>
        <Input id="admin-email" type="email" autoComplete="username" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="lc-input" data-testid="admin-email-input" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="admin-password">Password</Label>
        <Input id="admin-password" type="password" autoComplete="current-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="lc-input" data-testid="admin-password-input" />
      </div>
      {state.error && <p className="text-sm text-[#ff8a3d]" role="alert" data-testid="admin-login-error">{state.error}</p>}
      <button type="submit" disabled={state.loading} className="lc-btn-primary w-full" data-testid="admin-login-submit">
        {state.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
      </button>
    </form>
  );
};

const STATUS_CLS = { active: "bg-[#FF6A00]/15 text-[#ff8a3d]", past_due: "bg-red-500/15 text-red-300", paused: "bg-[#D4A437]/15 text-[#F3E0A2]", cancelled: "bg-white/10 text-[#A1A1AA]" };

const BillingRequests = ({ items, onDone }) => {
  const [busy, setBusy] = useState(null);
  if (!items?.length) return null;
  const done = async (id) => {
    setBusy(id);
    try {
      await http.post(`/admin/requests/${id}/done`, {}, auth());
      toast.success("Marked as done.");
      onDone();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="mt-4 rounded-2xl border border-[#D4A437]/40 bg-[#D4A437]/10 p-4" data-testid="admin-billing-requests">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#D4A437]">Update billing in your payment portal</p>
      <ul className="mt-2 space-y-2">
        {items.map((r) => (
          <li key={r.request_id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-[#F3E0A2]" data-testid={`admin-request-${r.request_id}`}>
            <span>{r.summary} <span className="text-xs text-[#8a8a8a]">({format(new Date(r.created_at), "d MMM, HH:mm")})</span></span>
            <button onClick={() => done(r.request_id)} disabled={busy === r.request_id} className="rounded-full border border-[#D4A437]/60 px-3 py-1 text-xs font-semibold hover:bg-[#D4A437]/20" data-testid={`admin-request-done-${r.request_id}`}>
              {busy === r.request_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="inline h-3 w-3" /> Done</>}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SubRow = ({ s, onSaved }) => {
  const [date, setDate] = useState(s.next_delivery_date || "");
  const [note, setNote] = useState(s.delivery_note || "");
  const [busy, setBusy] = useState(false);
  const [delivered, setDelivered] = useState(false);
  const [historyVersion, setHistoryVersion] = useState(0);
  const a = s.address || {};
  const save = async () => {
    if (!date) return toast.error("Choose a delivery date first.");
    setBusy(true);
    try {
      const { data } = await http.post(`/admin/subscriptions/${s.subscription_id}/delivery-date`, { date, note }, auth());
      toast.success(data.message);
      onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <article className="rounded-3xl border border-[#262626] bg-[#101010] p-5 sm:p-6" data-testid={`admin-sub-${s.subscription_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-bold">{s.name}</h2>
          <p className="text-sm text-[#A1A1AA]">{s.plan} · {formatGBP(s.price_pence)}/month · joined {format(new Date(s.started_at), "d MMM yyyy")}</p>
        </div>
        <span className={cn("rounded-full px-3 py-1 text-xs font-semibold", STATUS_CLS[s.status] || STATUS_CLS.cancelled)} data-testid="admin-sub-status">
          {s.payment_failed ? "Payment failed" : s.status.replace("_", " ")}
        </span>
      </div>
      <div className="mt-4 grid gap-2 text-sm text-[#F7F5F0]/85 sm:grid-cols-3">
        <p className="flex gap-2"><MapPin className="h-4 w-4 shrink-0 text-[#D4A437]" />{[a.line1, a.line2, a.town, a.postcode].filter(Boolean).join(", ")}</p>
        <a href={`tel:${s.phone}`} className="flex gap-2 hover:text-[#FF6A00]"><Phone className="h-4 w-4 text-[#D4A437]" />{s.phone}</a>
        <a href={`mailto:${s.email}`} className="flex gap-2 break-all hover:text-[#FF6A00]"><Mail className="h-4 w-4 shrink-0 text-[#D4A437]" />{s.email}</a>
      </div>
      {s.instructions && <p className="mt-2 text-sm text-[#A1A1AA]">Instructions: {s.instructions}</p>}
      {s.private_notes && <p className="mt-2 whitespace-pre-line rounded-xl border border-[#D4A437]/30 bg-[#D4A437]/5 px-3 py-2 text-sm text-[#F3E0A2]" data-testid={`sub-private-notes-${s.subscription_id}`}>Your notes: {s.private_notes}</p>}
      {(s.cancel_at_period_end || s.pause_until || s.pending_plan) && (
        <p className="mt-2 text-sm text-[#F3E0A2]" data-testid="admin-sub-flags">
          {[s.cancel_at_period_end && s.status !== "cancelled" && "Cancelling at next billing date", s.pause_until && `Paused until ${format(new Date(s.pause_until), "d MMM yyyy")}`, s.pending_plan && `Switching to ${s.pending_plan}`].filter(Boolean).join(" · ")}
        </p>
      )}
      <BillingRequests items={s.billing_requests} onDone={onSaved} />
      {s.provider === "teya" && (
        <p className={`mt-2 text-sm ${s.payment_failed ? "text-red-300" : "text-[#A1A1AA]"}`} data-testid="admin-sub-billing">
          {s.has_saved_card ? `Card on file${s.card_last4 ? ` ${s.card_brand || ""} •••• ${s.card_last4}` : ""}` : "No saved card yet"}
          {s.payment_failed && ` · Payment failed ${s.billing_attempts}x${s.last_failure_reason ? ` (${s.last_failure_reason})` : ""}${s.billing_gave_up ? " · retries stopped" : ""}`}
        </p>
      )}
      <div className="mt-5 grid gap-3 rounded-2xl bg-[#0a0a0a] p-4 sm:grid-cols-[180px_1fr_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor={`d-${s.subscription_id}`}>Delivery date</Label>
          <Input id={`d-${s.subscription_id}`} type="date" value={date} min={format(new Date(), "yyyy-MM-dd")} onChange={(e) => setDate(e.target.value)} className="lc-input [color-scheme:dark]" data-testid="admin-delivery-date-input" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`n-${s.subscription_id}`}>Note for customer (optional)</Label>
          <Input id={`n-${s.subscription_id}`} value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Morning delivery" className="lc-input" data-testid="admin-delivery-note-input" />
        </div>
        <button onClick={save} disabled={busy} className="lc-btn-primary !py-3" data-testid="admin-delivery-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Truck className="h-4 w-4" /> Save & email</>}
        </button>
      </div>
      {s.next_delivery_date && <p className="mt-2 text-xs text-[#8a8a8a]" data-testid="admin-current-delivery">Booked: {format(new Date(`${s.next_delivery_date}T12:00:00`), "EEEE d MMMM yyyy")}</p>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[#A1A1AA]" data-testid="admin-last-delivered">
          {s.last_delivered_date ? `Last delivered: ${format(new Date(`${s.last_delivered_date}T12:00:00`), "EEEE d MMMM yyyy")}` : "No deliveries recorded yet"}
        </p>
        <button onClick={() => setDelivered(true)} className="inline-flex items-center gap-2 rounded-full border border-[#22C55E]/60 px-4 py-2 text-sm font-semibold text-[#86efac] hover:bg-[#22C55E]/10" data-testid="admin-mark-delivered">
          <CheckCircle2 className="h-4 w-4" /> Mark as delivered
        </button>
      </div>
      <AdminHistory s={s} version={historyVersion} />
      {delivered && <DeliveredDialog s={s} open onOpenChange={setDelivered} onDone={() => { setHistoryVersion((v) => v + 1); onSaved(); }} auth={auth} />}
    </article>
  );
};

const Referrals = () => {
  const [r, setR] = useState(null);
  useEffect(() => {
    http.get("/admin/referrals", auth()).then(({ data }) => setR(data)).catch(() => setR(null));
  }, []);
  if (!r) return null;
  const sum = (o) => Object.values(o || {}).reduce((a, b) => a + b, 0);
  const fromK = (o) => Object.entries(o || {}).filter(([k]) => k.includes("kingsmoss")).reduce((a, [, v]) => a + v, 0);
  const stats = [
    ["Clicks to Kingsmoss Logs NI", sum(r.outbound_clicks), "referral-clicks"],
    ["Visits from Kingsmoss links", fromK(r.visits_by_source), "referral-visits"],
    ["Checkouts started from Kingsmoss", fromK(r.checkouts_by_source), "referral-checkouts"],
    ["Subscriptions from Kingsmoss", fromK(r.subscriptions_by_source), "referral-subscriptions"],
  ];
  return (
    <div className="mt-8 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="admin-referrals">
      {stats.map(([label, n, id]) => (
        <div key={id} className="rounded-2xl border border-[#262626] bg-[#101010] p-4">
          <p className="text-xs text-[#A1A1AA]">{label}</p>
          <p className="mt-1 font-display text-2xl font-bold text-[#F7F5F0]" data-testid={id}>{n}</p>
        </div>
      ))}
    </div>
  );
};

export default function Admin() {
  const [authed, setAuthed] = useState(!!sessionStorage.getItem(TOKEN_KEY));
  const [subs, setSubs] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/admin/subscriptions", auth());
      setSubs(data);
    } catch (err) {
      if ([401, 403].includes(err?.response?.status)) {
        sessionStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
      }
      toast.error(errorMessage(err));
    }
  }, []);
  useEffect(() => {
    if (authed) load();
  }, [authed, load]);
  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setAuthed(false);
    setSubs(null);
  };

  return (
    <div className="lc-container pt-28 pb-20" data-testid="admin-page">
      <Seo title="Admin" noindex />
      {!authed ? <Login onDone={() => setAuthed(true)} /> : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="lc-eyebrow">Admin</p>
              <h1 className="mt-2 font-display text-4xl font-extrabold">Subscribers & deliveries</h1>
            </div>
            <div className="flex gap-2">
              <button onClick={load} className="lc-btn-ghost !py-2.5" data-testid="admin-refresh"><RefreshCw className="h-4 w-4" /> Refresh</button>
              <button onClick={logout} className="lc-btn-ghost !py-2.5" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sign out</button>
            </div>
          </div>
          <Referrals />
          <CustomPackages auth={auth} />
          <div className="mt-8 space-y-4">
            {subs === null && <Loader2 className="h-6 w-6 animate-spin text-[#FF6A00]" />}
            {subs?.length === 0 && (
              <p className="rounded-3xl border border-dashed border-[#333] p-8 text-[#A1A1AA]" data-testid="admin-empty">No subscribers yet. New subscribers appear here as soon as their first payment is confirmed.</p>
            )}
            {subs?.map((s) => <SubRow key={s.subscription_id} s={s} onSaved={load} />)}
          </div>
        </>
      )}
    </div>
  );
}
